import axios, {
	InternalAxiosRequestConfig,
	AxiosResponse,
	AxiosError,
} from 'axios';
import { store } from '../Store';
import { ApiResponse, CustomError } from './types';
import { supabase } from '../supabaseClient';
import { resetUser } from '../redux/user/UserSlice';
import { clearAccessToken } from '../redux/auth/AuthSlice';

// Create an instance of Axios with common headers
const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_backend_api_url,
	// Add your common headers here
	headers: {
		'Content-Type': 'application/json',
		// Add any other headers you want to include in all requests
	},
});

export const axiosFormPost = (
	url: string,
	formData: FormData,
	setFileProgress: (fileprogress: number) => void
) => {
	return axiosInstance.post(url, formData, {
		headers: addSecureHeaders({
			'Content-Type': 'multipart/form-data',
		}),
		onUploadProgress: (progressEvent) => {
			const { loaded, total } = progressEvent;
			if (total) {
				const percentCompleted = Math.round((loaded * 100) / total);
				setFileProgress(percentCompleted);
			}
		},
	});
};

export const addSecureHeaders = (headers: any = {}) => {
	const accessToken = headers?.token || store?.getState()?.auth?.accessToken;

	if (accessToken) {
		// Use type assertion to let TypeScript know that headers is not undefined
		(headers as any).Authorization = `Bearer ${accessToken}`;
	}
	return headers;
};

// Add an Axios interceptor to set the Authorization header with the access token
axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	addSecureHeaders(config.headers);
	return config;
});

// Add an Axios interceptor to handle errors globally
axiosInstance.interceptors.response.use(
	(response: AxiosResponse<ApiResponse>) => response,
	async (error: AxiosError<ApiResponse>) => {
		if (error.response) {
			// Extract the error details from the response
			const { status, data } = error.response;

			// If the error is 401, the access token is invalid or expired, or unauthorized access
			if (status === 401) {
				try {
					const { data: sessionData, error: sessionError } =
						await supabase.auth.getSession();

					// If the session is invalid or expired, log out and redirect to login page
					if (!sessionData.session || sessionError) {
						console.warn('Session expired. Logging out...');
						alert('Session expired. Please log in again.');
						await supabase.auth.signOut(); // Ensure backend session is cleared
						localStorage.clear();
						sessionStorage.clear();
						store.dispatch(clearAccessToken());
						store.dispatch(resetUser());
						window.location.href = '/login'; // Redirect to login
						return Promise.reject(error);
					}
				} catch (sessionCheckError) {
					console.error('Error checking session:', sessionCheckError);
					await supabase.auth.signOut();
					window.location.href = '/login';
					return Promise.reject(error);
				}
			}

			// Check if data exists before accessing its properties
			const errorMessage =
				data && data.error && data.error.message
					? data.error.message
					: error.message;
			const errorCode =
				data && data.error && data.error.code ? data.error.code : error.code;

			// Throw the custom error with status to be handled by the caller of the API function
			throw new CustomError(errorMessage, errorCode, status);
		} else {
			// For network errors, handle them here
			console.error('Network Error:', error.message);

			// Throw the custom error with status to be handled by the caller of the API function
			throw new CustomError('Network error occurred.', 'NETWORK_ERROR', 500);
		}
	}
);

export default axiosInstance;
